/**
 * Agent 执行详情页 — 真实 SSE 流式执行
 * 使用真实 API: POST /api/agents/:id/execute/stream
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchAgent, AgentDTO } from '../../api/agent';
import { createStreamExecution } from '../../api/execution';

type ExecStatus = 'IDLE' | 'CONNECTING' | 'RUNNING' | 'TOOL_CALLING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';

const STATUS_CFG: Record<ExecStatus, { label: string; color: string }> = {
  IDLE: { label: '待执行', color: '#6b7280' },
  CONNECTING: { label: '连接中', color: '#818cf8' },
  RUNNING: { label: '执行中', color: '#6366f1' },
  TOOL_CALLING: { label: '工具调用', color: '#fbbf24' },
  COMPLETED: { label: '已完成', color: '#34d399' },
  FAILED: { label: '失败', color: '#f87171' },
  CANCELLED: { label: '已取消', color: '#6b7280' },
};

interface ToolCall {
  tool: string;
  args: Record<string, unknown>;
  result?: string;
}

export default function AgentExecutionPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const agentId = id || '';
  const autoRun = false;

  const [agent, setAgent] = useState<AgentDTO | null>(null);
  const [input, setInput] = useState('');
  const [status, setStatus] = useState<ExecStatus>('IDLE');
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');
  const [toolCalls, setToolCalls] = useState<ToolCall[]>([]);
  const [metrics, setMetrics] = useState({ tokens: 0, durationMs: 0, provider: '', toolCount: 0 });

  const abortRef = useRef<(() => void) | null>(null);
  const startTimeRef = useRef(0);
  const outputRef = useRef('');

  // Load agent details
  useEffect(() => {
    if (!agentId) return;
    fetchAgent(agentId)
      .then((data) => setAgent(data))
      .catch(() => setAgent(null));
  }, [agentId]);

  const handleExecute = useCallback(() => {
    if (!input.trim() || !agentId) return;

    setStatus('CONNECTING');
    setOutput('');
    setError('');
    setToolCalls([]);
    outputRef.current = '';
    startTimeRef.current = Date.now();

    const { abort } = createStreamExecution(
      agentId,
      { input: input.trim() },
      {
        onConnected: () => setStatus('RUNNING'),
        onStarted: () => setStatus('RUNNING'),
        onProgress: (data: Record<string, unknown>) => {
          const delta = (data.delta as string) || '';
          outputRef.current += delta;
          setOutput(outputRef.current);
          setMetrics((m) => ({ ...m, tokens: (data.tokens as number) || m.tokens }));
        },
        onToolCall: (data: Record<string, unknown>) => {
          setStatus('TOOL_CALLING');
          setToolCalls((prev) => [
            ...prev,
            { tool: (data.tool as string) || 'unknown', args: (data.arguments as Record<string, unknown>) || {} },
          ]);
        },
        onToolResult: (data: Record<string, unknown>) => {
          setToolCalls((prev) =>
            prev.map((t) => (t.tool === data.tool ? { ...t, result: String(data.result || '') } : t))
          );
        },
        onCompleted: (data: Record<string, unknown>) => {
          setStatus('COMPLETED');
          setMetrics((m) => ({
            ...m,
            tokens: (data.totalTokens as number) || m.tokens,
            durationMs: (data.durationMs as number) || (Date.now() - startTimeRef.current),
            provider: (data.provider as string) || m.provider,
            toolCount: toolCalls.length,
          }));
        },
        onError: (data: Record<string, unknown>) => {
          setStatus('FAILED');
          setError((data.message as string) || '执行失败');
        },
        onConnectionError: () => {
          setStatus('FAILED');
          setError('网络连接失败');
        },
      }
    );

    abortRef.current = abort;
  }, [agentId, input, toolCalls.length]);

  const handleStop = () => {
    abortRef.current?.();
    abortRef.current = null;
    setStatus('CANCELLED');
  };

  const currentStatusCfg = STATUS_CFG[status];

  return (
    <div style={styles.page}>
      <div style={styles.topBar}>
        <button style={styles.backBtn} onClick={() => navigate('/simon-cat/agents')}>
          ← 返回
        </button>
        <div style={styles.agentInfo}>
          <span style={styles.agentAvatar}>{agent?.avatar || '🤖'}</span>
          <div>
            <h2 style={styles.agentName}>{agent?.name || agentId || 'Agent'}</h2>
            <span style={styles.agentModel}>{agent?.model || '加载中...'}</span>
          </div>
        </div>
        <span style={{ ...styles.statusBadge, color: currentStatusCfg.color }}>
          {currentStatusCfg.label}
        </span>
      </div>

      {/* Metrics */}
      {status !== 'IDLE' && (
        <div style={styles.metricsBar}>
          <div style={styles.metric}>
            <span style={styles.metricLabel}>状态</span>
            <span style={{ ...styles.metricVal, color: currentStatusCfg.color }}>{currentStatusCfg.label}</span>
          </div>
          <div style={styles.metric}>
            <span style={styles.metricLabel}>耗时</span>
            <span style={styles.metricVal}>
              {metrics.durationMs > 0
                ? `${(metrics.durationMs / 1000).toFixed(1)}s`
                : status === 'RUNNING' || status === 'TOOL_CALLING'
                  ? `${((Date.now() - startTimeRef.current) / 1000).toFixed(1)}s`
                  : '-'}
            </span>
          </div>
          <div style={styles.metric}>
            <span style={styles.metricLabel}>Token</span>
            <span style={styles.metricVal}>{metrics.tokens || '-'}</span>
          </div>
          <div style={styles.metric}>
            <span style={styles.metricLabel}>Provider</span>
            <span style={styles.metricVal}>{metrics.provider || '-'}</span>
          </div>
        </div>
      )}

      {/* Input Area */}
      <div style={styles.inputArea}>
        <textarea
          style={styles.input}
          placeholder="输入你的问题..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          rows={3}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleExecute();
            }
          }}
        />
        <div style={styles.inputActions}>
          {status === 'RUNNING' || status === 'TOOL_CALLING' ? (
            <button style={styles.stopBtn} onClick={handleStop}>
              ⏹ 停止
            </button>
          ) : (
            <button
              style={{ ...styles.executeBtn, ...(input.trim() ? {} : styles.executeBtnDisabled) }}
              onClick={handleExecute}
              disabled={!input.trim()}
            >
              ⚡ 执行
            </button>
          )}
        </div>
      </div>

      {/* Output */}
      {output && (
        <div style={styles.outputCard}>
          <div style={styles.outputHeader}>
            <span style={styles.outputTitle}>📋 执行结果</span>
            {(status === 'RUNNING' || status === 'TOOL_CALLING') && (
              <span style={styles.streamingBadge}>● 流式输出中</span>
            )}
          </div>
          <pre style={styles.outputPre}>
            {output}
            {(status === 'RUNNING' || status === 'TOOL_CALLING') && <span style={styles.cursor}>▊</span>}
          </pre>
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={styles.outputCard}>
          <pre style={styles.errorOutput}>❌ {error}</pre>
        </div>
      )}

      {/* Tool Calls */}
      {toolCalls.length > 0 && (
        <div style={styles.toolCard}>
          <span style={styles.toolTitle}>🔧 工具调用 ({toolCalls.length})</span>
          <div style={styles.toolList}>
            {toolCalls.map((tc, idx) => (
              <div key={idx} style={styles.toolItem}>
                <div style={styles.toolItemHeader}>
                  <span style={styles.toolName}>{tc.tool}</span>
                  <span style={styles.toolStatus}>{tc.result ? '✓ 完成' : '⏳ 执行中'}</span>
                </div>
                {tc.result && (
                  <div style={styles.toolResult}>
                    {tc.result.substring(0, 200)}{tc.result.length > 200 ? '...' : ''}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: { maxWidth: 900, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 14 },
  topBar: { display: 'flex', alignItems: 'center', gap: 14 },
  backBtn: {
    background: 'transparent', border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 6, padding: '6px 10px', color: '#94a3b8', fontSize: 12, cursor: 'pointer',
  },
  agentInfo: { flex: 1, display: 'flex', alignItems: 'center', gap: 10 },
  agentAvatar: { fontSize: 28 },
  agentName: { fontSize: 16, fontWeight: 600, margin: 0 },
  agentModel: { fontSize: 11, color: '#64748b' },
  statusBadge: { fontSize: 12, fontWeight: 600 },
  metricsBar: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 },
  metric: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
    padding: '8px 4px', borderRadius: 8, background: 'rgba(255,255,255,0.025)',
    border: '1px solid rgba(255,255,255,0.06)',
  },
  metricLabel: { fontSize: 10, color: '#64748b' },
  metricVal: { fontSize: 14, fontWeight: 600 },
  inputArea: { display: 'flex', flexDirection: 'column', gap: 8 },
  input: {
    width: '100%', padding: '12px 14px', borderRadius: 10,
    border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.03)',
    color: '#e2e8f0', fontSize: 13, resize: 'none', outline: 'none',
    fontFamily: 'inherit',
  },
  inputActions: { display: 'flex', justifyContent: 'flex-end' },
  executeBtn: {
    padding: '7px 20px', borderRadius: 7, border: '1px solid #6366f1',
    background: 'rgba(99,102,241,0.15)', color: '#a5b4fc', fontSize: 12, fontWeight: 600, cursor: 'pointer',
  },
  executeBtnDisabled: { opacity: 0.4, cursor: 'not-allowed' },
  stopBtn: {
    padding: '7px 20px', borderRadius: 7, border: '1px solid #f87171',
    background: 'rgba(248,113,113,0.15)', color: '#f87171', fontSize: 12, fontWeight: 600, cursor: 'pointer',
  },
  outputCard: {
    padding: 14, borderRadius: 10,
    background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.06)',
  },
  outputHeader: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 },
  outputTitle: { fontSize: 12, fontWeight: 600 },
  streamingBadge: { fontSize: 10, color: '#34d399' },
  outputPre: {
    whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12, lineHeight: 1.7,
    margin: 0, fontFamily: 'monospace', color: '#e2e8f0',
  },
  errorOutput: { whiteSpace: 'pre-wrap', fontSize: 12, color: '#f87171', margin: 0, fontFamily: 'monospace' },
  cursor: { display: 'inline-block', animation: 'blink 1s infinite', color: '#6366f1' },
  toolCard: {
    padding: 14, borderRadius: 10,
    background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.06)',
  },
  toolTitle: { fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 8 },
  toolList: { display: 'flex', flexDirection: 'column', gap: 6 },
  toolItem: { padding: '8px 10px', borderRadius: 6, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' },
  toolItemHeader: { display: 'flex', alignItems: 'center', gap: 6 },
  toolName: { fontSize: 11, fontWeight: 600, fontFamily: 'monospace' },
  toolStatus: { marginLeft: 'auto', fontSize: 10, color: '#64748b' },
  toolResult: { fontSize: 10, marginTop: 4, padding: '3px 6px', borderRadius: 4, background: 'rgba(52,211,153,0.05)' },
};
